import {
  isCompositeSliceContent,
  isSharedSliceContent,
  isSimpleSliceContent,
  SliceItemContent,
  SlicesContent
} from "@prismicio/types-internal/lib/content"
import type { StaticSlices } from "@prismicio/types-internal/lib/customtypes"

import { useEditorConfig } from "../EditorConfig"
import { CompositeSliceEditor } from "../slices/CompositeSliceEditor"
import { LegacySliceEditor } from "../slices/LegacySliceEditor"
import { SharedSliceEditor } from "../slices/SharedSliceEditor"
import { LegacyCustomLabelSelect } from "./Components/LegacyCustomLabelSelect"

interface SliceZoneEditorProps {
  id: string
  readOnly: boolean
  sliceZone: StaticSlices
  content: SlicesContent | undefined
  onContentChange: (content: SlicesContent) => void
}

export function SliceZoneEditor(props: SliceZoneEditorProps) {
  const { content, id, onContentChange, readOnly, sliceZone } = props

  const editorConfig = useEditorConfig()

  return (
    <>
      {content?.value.map((sliceContent, sliceContentIndex) => {
        const slice = sliceZone.config?.choices?.[sliceContent.name]
        if (slice === undefined) return null

        const widgetContent = sliceContent.widget

        const sliceProps = {
          id: `${id}[${sliceContent.key}]`,
          key: sliceContent.key,
          sliceKey: sliceContent.key,
          onContentChange: (newWidgetContent: SliceItemContent["widget"]) => {
            const newSliceContent = {
              ...sliceContent,
              widget: newWidgetContent
            }
            const newContent = updateSlices(content, [
              sliceContentIndex,
              newSliceContent
            ])
            onContentChange(newContent)
          },
          readOnly
        }

        if (slice.type === "SharedSlice") {
          return isSharedSliceContent(widgetContent) ? (
            <SharedSliceEditor
              {...sliceProps}
              config={editorConfig}
              sharedSlice={slice}
              content={widgetContent}
            />
          ) : null
        } else if (slice.type === "Slice") {
          return isCompositeSliceContent(widgetContent) ? (
            <CompositeSliceEditor
              {...sliceProps}
              compositeSlice={slice}
              content={widgetContent}
              header={
                <LegacyCustomLabelSelect
                  sliceContent={sliceContent}
                  sliceZone={sliceZone}
                  slices={content}
                  onChange={onContentChange}
                />
              }
            />
          ) : null
        } else {
          return isSimpleSliceContent(widgetContent) ? (
            <LegacySliceEditor
              {...sliceProps}
              legacySlice={slice}
              content={widgetContent}
              header={
                <LegacyCustomLabelSelect
                  sliceContent={sliceContent}
                  sliceZone={sliceZone}
                  slices={content}
                  onChange={onContentChange}
                />
              }
            />
          ) : null
        }
      })}
    </>
  )
}

function updateSlices(
  slices: SlicesContent,
  [index, slice]: [number, SliceItemContent]
): SlicesContent {
  return {
    __TYPE__: "SliceContentType",
    value: slices.value.map((candidateSlice, candidateSliceIndex) =>
      candidateSliceIndex === index ? slice : candidateSlice
    )
  }
}
